import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";

import { useAuth } from "../src/auth";
import { AppBar, Coin, Eyebrow } from "../src/components/brand";
import { colors, fonts, radius, space } from "../src/theme";

// App home — the calculator chooser. Each tool lives on its own route
// (/au08, /au06, /au13); this hub is the neutral landing for deep links that
// don't name a tool (the marketing "Check your numbers" CTA) and the home
// screen for the native app, which has no marketing site behind it. Generic
// information-only framing only (validation §3); each calculator cites its own
// sources on its result screen.

type CalcRoute =
  | "/au06"
  | "/take-home"
  | "/deduction-method"
  | "/side-income"
  | "/packaging-shock"
  | "/cgt"
  | "/au13"
  | "/family-payments"
  | "/ccs-buffer"
  | "/second-earner"
  | "/rent-assistance"
  | "/super-boost"
  | "/lost-super"
  | "/default-super-insurance"
  | "/stamp-duty"
  | "/first-home-scheme"
  | "/lmi"
  | "/borrowing-power"
  | "/fhss"
  | "/home-buying-cost"
  | "/energy-overpayment"
  | "/solar-battery"
  | "/credit-card-payoff"
  | "/au08"
  | "/wills-intestacy";

interface CalcCard {
  route: CalcRoute;
  icon: string;
  title: string;
  role: string;
  blurb: string;
  testID: string;
}

interface CalcGroup {
  category: string;
  calcs: CalcCard[];
}

// dir slug (route without the leading "/") → testID, keeping the existing
// choose-<dir> convention for every tool.
const card = (
  route: CalcRoute,
  icon: string,
  title: string,
  role: string,
  blurb: string,
): CalcCard => ({ route, icon, title, role, blurb, testID: `choose-${route.slice(1)}` });

const GROUPS: CalcGroup[] = [
  {
    category: "Tax & income",
    calcs: [
      card(
        "/au06",
        "↗",
        "HECS / HELP",
        "Repayment tracker",
        "See your compulsory study and training loan repayment under the new FY26 marginal system.",
      ),
      card(
        "/take-home",
        "$",
        "Take-home pay",
        "After-tax pay",
        "See what actually lands in your account — and what a pay rise really adds once tax and HECS take their cut.",
      ),
      card(
        "/deduction-method",
        "⌂",
        "Work-from-home deductions",
        "Method compared",
        "Compare the fixed-rate and actual-cost methods side by side for your home-office claim.",
      ),
      card(
        "/side-income",
        "+",
        "Side & gig income",
        "Tax to set aside",
        "Work out the tax to put aside on side or gig income, and when GST registration starts to apply.",
      ),
      card(
        "/packaging-shock",
        "≈",
        "Salary-packaging flow-on",
        "Threshold effects",
        "See how salary packaging moves the income-tested thresholds that drive your other entitlements.",
      ),
      card(
        "/cgt",
        "△",
        "Capital gains on a sale",
        "Gain & discount",
        "Work out the gain on a sale, the 50% discount where it applies, and the tax that follows.",
      ),
    ],
  },
  {
    category: "Family & benefits",
    calcs: [
      card(
        "/au13",
        "★",
        "Childcare Subsidy",
        "CCS estimate & reclaim",
        "Estimate your Child Care Subsidy, and check a past year while the reclaim window is open.",
      ),
      card(
        "/family-payments",
        "♦",
        "Family Tax Benefit + CCS",
        "Family payments",
        "Estimate your family payments — Family Tax Benefit alongside the Child Care Subsidy.",
      ),
      card(
        "/ccs-buffer",
        "◷",
        "CCS withholding buffer",
        "Balancing position",
        "See your likely balancing position at tax time given the standard CCS withholding.",
      ),
      card(
        "/second-earner",
        "⇉",
        "The “extra day”",
        "Second-earner net",
        "See what a second earner’s extra work day nets once income tax and the CCS taper are applied.",
      ),
      card(
        "/rent-assistance",
        "⌂",
        "Rent Assistance",
        "CRA estimate",
        "Estimate your Commonwealth Rent Assistance based on your rent and household.",
      ),
    ],
  },
  {
    category: "Super",
    calcs: [
      card(
        "/super-boost",
        "▲",
        "Super you may be missing",
        "Contributions room",
        "See co-contribution, spouse offset and carry-forward room you may be eligible for.",
      ),
      card(
        "/lost-super",
        "⊗",
        "Lost & duplicate super",
        "Fee drag",
        "See the yearly fee drag of holding extra or duplicate super accounts.",
      ),
      card(
        "/default-super-insurance",
        "⛉",
        "Insurance in your super",
        "Default cover rules",
        "Check whether the default insurance-in-super rules apply to your account.",
      ),
    ],
  },
  {
    category: "Buying a home",
    calcs: [
      card(
        "/stamp-duty",
        "▣",
        "Stamp duty",
        "Transfer duty by state",
        "Work out transfer duty and any first-home concession for your state.",
      ),
      card(
        "/first-home-scheme",
        "⚑",
        "First-home schemes",
        "Eligibility",
        "See which first-home grants and schemes you appear eligible for.",
      ),
      card(
        "/lmi",
        "⛨",
        "Lenders Mortgage Insurance",
        "Indicative cost",
        "See the indicative cost of Lenders Mortgage Insurance and when it’s waived.",
      ),
      card(
        "/borrowing-power",
        "↕",
        "Borrowing power",
        "Indicative estimate",
        "Get an indicative borrowing estimate — and see the invisible APRA levers behind it.",
      ),
      card(
        "/fhss",
        "▲",
        "First Home Super Saver",
        "Release & tax effect",
        "See what you could release under the First Home Super Saver scheme and the tax effect.",
      ),
      card(
        "/home-buying-cost",
        "Σ",
        "True cost to buy",
        "All-in upfront cash",
        "See the all-in upfront cash to buy, itemised line by line.",
      ),
    ],
  },
  {
    category: "Cost of living & debt",
    calcs: [
      card(
        "/energy-overpayment",
        "⚡",
        "Electricity check",
        "Plan vs reference",
        "Compare your electricity plan against the regulated reference price.",
      ),
      card(
        "/solar-battery",
        "☀",
        "Home battery rebate",
        "Discount & payback",
        "See the indicative federal battery discount and an estimated payback range.",
      ),
      card(
        "/credit-card-payoff",
        "✂",
        "Credit-card payoff",
        "Interest & timeline",
        "See months to clear, total interest, and the minimum-payment trap on a card balance.",
      ),
    ],
  },
  {
    category: "Health",
    calcs: [
      card(
        "/au08",
        "+",
        "Private health",
        "MLS & LHC loading",
        "Work out your Medicare Levy Surcharge and Lifetime Health Cover loading for FY26.",
      ),
    ],
  },
  {
    category: "Estate",
    calcs: [
      card(
        "/wills-intestacy",
        "§",
        "No will?",
        "Intestacy & readiness",
        "See who inherits under the intestacy rules, with an estate-readiness check.",
      ),
    ],
  },
];

export default function Home(): JSX.Element {
  const router = useRouter();
  const { status } = useAuth();

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.headerRow}>
            <Eyebrow>Free · no login to start</Eyebrow>
            {status === "authed" ? (
              <Pressable onPress={() => router.push("/my-results")} accessibilityRole="button">
                <Text style={styles.navLink}>My results</Text>
              </Pressable>
            ) : status === "anon" ? (
              <Pressable onPress={() => router.push("/auth")} accessibilityRole="button">
                <Text style={styles.navLink}>Sign in</Text>
              </Pressable>
            ) : null}
          </View>

          <Coin size={64} />
          <Text style={styles.title}>The numbers no one explains.</Text>
          <Text style={styles.body}>
            Plain-English checks for the money the ATO, your super fund, your
            health fund and Services Australia make hard — tax, family payments,
            super, buying a home, and more. Pick one: each takes about a minute
            and shows its working.
          </Text>

          {GROUPS.map((group) => (
            <View key={group.category} style={styles.group}>
              <Text style={styles.groupHeading}>{group.category}</Text>
              <View style={styles.cardList}>
                {group.calcs.map((c) => (
                  <Pressable
                    key={c.route}
                    onPress={() => router.push(c.route)}
                    accessibilityRole="button"
                    accessibilityLabel={`${c.title} — ${c.role}`}
                    testID={c.testID}
                    style={({ pressed }) => [
                      styles.card,
                      pressed && { transform: [{ translateY: 1 }] },
                    ]}
                  >
                    <View style={styles.cardIcon}>
                      <Text style={styles.cardIconText}>{c.icon}</Text>
                    </View>
                    <View style={styles.cardBody}>
                      <Text style={styles.cardTitle}>{c.title}</Text>
                      <Text style={styles.cardRole}>{c.role}</Text>
                      <Text style={styles.cardBlurb}>{c.blurb}</Text>
                    </View>
                    <Text style={styles.cardChevron}>→</Text>
                  </Pressable>
                ))}
              </View>
            </View>
          ))}

          <Text style={styles.fine}>
            Information only · not financial advice · sources shown with every
            result
          </Text>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 680, gap: space.lg },

  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },

  title: {
    fontFamily: fonts.display,
    fontSize: 34,
    lineHeight: 38,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.sm,
  },
  body: { fontFamily: fonts.body, fontSize: 17, lineHeight: 25, color: colors.inkSoft },

  group: { gap: space.sm, marginTop: space.sm },
  groupHeading: {
    fontFamily: fonts.mono,
    fontSize: 12,
    letterSpacing: 1,
    textTransform: "uppercase",
    color: colors.honeyDeep,
  },
  cardList: { gap: space.md },
  card: {
    flexDirection: "row",
    alignItems: "center",
    gap: space.lg,
    padding: space.lg,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.line,
    backgroundColor: colors.card,
  },
  cardIcon: {
    width: 48,
    height: 48,
    borderRadius: radius.md,
    backgroundColor: colors.honeyTint,
    alignItems: "center",
    justifyContent: "center",
  },
  cardIconText: { fontFamily: fonts.displayHeavy, fontSize: 24, color: colors.honeyDeep },
  cardBody: { flex: 1, gap: 2 },
  cardTitle: { fontFamily: fonts.display, fontSize: 19, letterSpacing: -0.4, color: colors.ink },
  cardRole: {
    fontFamily: fonts.mono,
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: colors.muted,
  },
  cardBlurb: {
    fontFamily: fonts.body,
    fontSize: 14,
    lineHeight: 20,
    color: colors.inkSoft,
    marginTop: 4,
  },
  cardChevron: { fontFamily: fonts.bodyBold, fontSize: 20, color: colors.honeyDeep },

  fine: { fontFamily: fonts.mono, fontSize: 11.5, color: colors.muted, marginTop: space.xs },
});
